import * as React from "react";
import { PanelContext } from "./PanelContext";
import type { TypePanelContext, TypePanelProviderProps } from "./PanelTypes";

export const PanelProvider = ({
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
}: TypePanelProviderProps) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isPanelOpen = isControlled ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const openPanel = React.useCallback(() => setOpen(true), [setOpen]);
  const closePanel = React.useCallback(() => setOpen(false), [setOpen]);
  const togglePanel = React.useCallback(
    () => setOpen(!isPanelOpen),
    [setOpen, isPanelOpen]
  );

  const value = React.useMemo<TypePanelContext>(
    () => ({ isPanelOpen, openPanel, closePanel, togglePanel }),
    [isPanelOpen, openPanel, closePanel, togglePanel]
  );

  return (
    <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
  );
};
